import { X, Bell, CheckCircle, AlertTriangle, AlertOctagon, Info } from "lucide-react";
import { toast } from "sonner";

interface NotificationToastProps {
  id: string | number;
  title: string;
  message?: string;
  severity?: "info" | "success" | "warning" | "urgent";
  link?: string | null;
}

const severityConfig = {
  info: {
    bar: "bg-blue-500",
    icon: Info,
    iconColor: "text-blue-400",
    bg: "bg-blue-500/10",
  },
  success: {
    bar: "bg-emerald-500",
    icon: CheckCircle,
    iconColor: "text-emerald-400",
    bg: "bg-emerald-500/10",
  },
  warning: {
    bar: "bg-amber-500",
    icon: AlertTriangle,
    iconColor: "text-amber-400",
    bg: "bg-amber-500/10",
  },
  urgent: {
    bar: "bg-red-500",
    icon: AlertOctagon,
    iconColor: "text-red-400",
    bg: "bg-red-500/10",
  },
};

export function NotificationToast({
  id,
  title,
  message,
  severity = "info",
  link,
}: NotificationToastProps) {
  const config = severityConfig[severity] ?? severityConfig.info;
  const Icon = config.icon;

  return (
    <div className="w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border bg-card shadow-2xl animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="flex">
        {/* Severity color bar */}
        <div className={`w-1.5 shrink-0 ${config.bar}`} />

        <div className="flex flex-1 items-start gap-3 p-3">
          {/* Icon */}
          <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.bg}`}>
            <Icon className={`h-4 w-4 ${config.iconColor}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight truncate">
              {title}
            </p>
            {message && (
              <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                {message}
              </p>
            )}
            <div className="mt-1.5 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground/70">Just now</span>
              {link && (
                <button
                  onClick={() => {
                    window.location.assign(link);
                    toast.dismiss(id);
                  }}
                  className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  View →
                </button>
              )}
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={() => toast.dismiss(id)}
            className="shrink-0 mt-0.5 p-0.5 rounded hover:bg-muted transition-colors"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
}
